import { z } from "zod";

import { GroupIds, INBOUND_NUMBER_TO_GROUP_ID } from "@/shared/group-configs/groups";
import { e164PhoneNumberSchema } from "@/shared/utils/phone-utils";
import { getVoiceInboundRoutingConfig } from "@/shared/voice-routing/inbound-routing";

export const VOICE_SIP_WEBHOOK_PATH = "/api/webhooks/twilio/voice/sip";
export const VOICE_SIP_WEBHOOK_URL = `https://voice-webhook.valeriehealth.com${VOICE_SIP_WEBHOOK_PATH}`;
export const SIP_INGRESS_HEADER = "X-Valerie-Ingress";
export const SIP_INGRESS_ATTRIBUTE = "valerie.sipIngress";

const domainKeySchema = z.string().regex(/^[a-z][a-z0-9-]{0,39}$/);

/** Twilio account SID: `AC` plus 32 hex digits. */
export const twilioAccountSidSchema = z.string().regex(/^AC[0-9a-fA-F]{32}$/);

export const sipIngressMetadataSchema = z
  .object({
    domainKey: domainKeySchema,
    originalCalledNumber: e164PhoneNumberSchema,
    forwardedFrom: e164PhoneNumberSchema.optional(),
  })
  .strict();
export type SipIngressMetadata = z.infer<typeof sipIngressMetadataSchema>;

// Persist caller identity separately from the legacy transcript phone column, which only
// accepts North American numbers. Keep the on-wire ingress header unchanged.
export const sipIngressSessionMetadataSchema = sipIngressMetadataSchema.extend({
  callerNumber: z.union([e164PhoneNumberSchema, z.literal("anonymous")]).optional(),
});

export const sipIngressDomainSchema = z
  .object({
    key: domainKeySchema,
    accountSid: twilioAccountSidSchema,
    // Null while provisioning. A domain cannot receive application traffic until its SID is pinned.
    domainSid: z
      .string()
      .regex(/^SD[0-9a-fA-F]{32}$/)
      .nullable(),
    domainName: z.string().regex(/^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.sip\.twilio\.com$/),
    groupId: z.nativeEnum(GroupIds),
    // A Twilio-owned voice number used when a SIP-originated call is dialed back to the PSTN.
    pstnCallerId: e164PhoneNumberSchema,
    routes: z
      .array(
        z
          .object({
            originalCalledNumber: e164PhoneNumberSchema,
            calledNumber: e164PhoneNumberSchema,
          })
          .strict()
      )
      .min(1),
  })
  .strict();
export type SipIngressDomain = z.infer<typeof sipIngressDomainSchema>;

export function encodeSipIngressMetadata(metadata: SipIngressMetadata): string {
  const parsed = sipIngressMetadataSchema.parse(metadata);

  // One bounded header avoids partial attribute updates and Twilio's 255-character URI limit.
  return JSON.stringify([parsed.domainKey, parsed.originalCalledNumber, parsed.forwardedFrom ?? null]);
}

export function decodeSipIngressMetadata(value: string): SipIngressMetadata | null {
  if (value.length > 128) return null;

  try {
    const [domainKey, originalCalledNumber, forwardedFrom] = z
      .tuple([domainKeySchema, e164PhoneNumberSchema, e164PhoneNumberSchema.nullable()])
      .parse(JSON.parse(value));

    return { domainKey, originalCalledNumber, ...(forwardedFrom && { forwardedFrom }) };
  } catch {
    return null;
  }
}

export function buildSipIngressDestination(uri: string, metadata?: SipIngressMetadata): string {
  // The destination must come from trusted routing configuration, never the caller's SIP URI.
  if (!parseSipAddress(uri)) throw new Error("Invalid SIP ingress destination");
  if (/:5060(?:;|$)|;transport=(?:tcp|udp)$/.test(uri))
    throw new Error("SIP ingress destination must support TLS on port 5061");

  // Twilio's secure flag encrypts both signaling and media; transport=tls alone leaves RTP clear.
  const headers = metadata ? `?${SIP_INGRESS_HEADER}=${encodeURIComponent(encodeSipIngressMetadata(metadata))}` : "";
  const result = `${uri};secure=true${headers}`;

  if (result.length >= 255) throw new Error("SIP ingress destination exceeds Twilio's URI limit");

  return result;
}

export function defineSipIngressDomains(input: readonly SipIngressDomain[]): readonly SipIngressDomain[] {
  const domains = z.array(sipIngressDomainSchema).parse(input);
  const keys = new Set<string>();
  const names = new Set<string>();
  const sids = new Set<string>();
  const routingNumbers = new Map<string, string>();

  for (const domain of domains) {
    if (keys.has(domain.key) || names.has(domain.domainName) || (domain.domainSid && sids.has(domain.domainSid)))
      throw new Error("Duplicate SIP ingress domain");
    keys.add(domain.key);
    names.add(domain.domainName);
    if (domain.domainSid) sids.add(domain.domainSid);
    const originals = new Set<string>();

    for (const route of domain.routes) {
      const config = getVoiceInboundRoutingConfig(route.calledNumber);

      if (
        !config ||
        config.groupId !== domain.groupId ||
        INBOUND_NUMBER_TO_GROUP_ID[route.calledNumber] !== domain.groupId
      )
        throw new Error(`SIP ingress route must belong to the configured group: ${domain.key}`);
      if (parseSipAddress(config.voiceAgentSipUri)?.user !== route.calledNumber)
        throw new Error(`LiveKit SIP destination must preserve the canonical routing number: ${domain.key}`);
      if (originals.has(route.originalCalledNumber)) throw new Error(`Duplicate SIP ingress route: ${domain.key}`);
      const owner = routingNumbers.get(route.calledNumber);

      if (owner && owner !== domain.key) throw new Error("SIP routing number belongs to multiple domains");
      originals.add(route.originalCalledNumber);
      routingNumbers.set(route.calledNumber, domain.key);
      buildSipIngressDestination(config.voiceAgentSipUri, {
        domainKey: domain.key,
        originalCalledNumber: route.originalCalledNumber,
        forwardedFrom: "+123456789012345",
      });
    }
  }

  return domains;
}

/** Deliberately narrow contract: number/anonymous user, hostname, optional port/transport. */
export function parseSipAddress(value: string): { user: string; host: string } | null {
  if (value.length >= 255 || value !== value.trim()) return null;
  const match =
    /^sips?:((?:\+|%2[Bb])[1-9]\d{1,14}|anonymous)@([a-zA-Z0-9.-]+)(?::506[01])?(?:;transport=(?:tls|tcp|udp))?$/.exec(
      value
    );

  if (!match) return null;

  return { user: match[1].replace(/^%2[Bb]/, "+"), host: match[2].toLowerCase() };
}

export function resolveSipIngress(
  params: Record<string, string>,
  domains: readonly SipIngressDomain[]
): { calledNumber: string; fromNumber: string; pstnCallerId: string; metadata: SipIngressMetadata } | null {
  const domain = domains.find(
    (entry) =>
      entry.domainSid !== null &&
      entry.domainSid === params.SipDomainSid &&
      entry.accountSid === params.AccountSid &&
      entry.domainName === params.SipDomain
  );
  const destination = parseSipAddress(params.To ?? "");
  // From is untrusted and only used for display, sampling and prewarm, so an unparseable
  // caller (non-standard port, IPv6 host, "Restricted", ...) is treated as anonymous.
  const fromNumber = parseSipAddress(params.From ?? "")?.user ?? "anonymous";

  if (
    !domain ||
    !destination ||
    destination.host !== domain.domainName ||
    !/^CA[0-9a-fA-F]{32}$/.test(params.CallSid ?? "")
  )
    return null;
  const route = domain.routes.find((entry) => entry.originalCalledNumber === destination.user);

  if (!route) return null;

  // SIP header names are case-insensitive. Reject ambiguous variants rather than choosing one.
  const originalHeaders = Object.entries(params).filter(
    ([key]) => key.toLowerCase() === "sipheader_x-original-called-number"
  );
  const forwardedHeaders = Object.entries(params).filter(([key]) => key.toLowerCase() === "sipheader_x-forwarded-from");

  if (originalHeaders.length > 1 || forwardedHeaders.length > 1) return null;

  const assertedOriginal = originalHeaders[0]?.[1];

  if (assertedOriginal !== undefined && assertedOriginal !== route.originalCalledNumber) return null;
  // Forwarding information is customer-asserted and informational, like From. Drop a malformed
  // value rather than rejecting the call.
  const forwardedFrom = e164PhoneNumberSchema.safeParse(forwardedHeaders[0]?.[1]).data;

  return {
    calledNumber: route.calledNumber,
    fromNumber,
    pstnCallerId: domain.pstnCallerId,
    metadata: {
      domainKey: domain.key,
      originalCalledNumber: route.originalCalledNumber,
      ...(forwardedFrom && { forwardedFrom }),
    },
  };
}
